import {useEffect, useState} from "react";
import {useSelector} from "react-redux";
import {css, Theme} from "@emotion/react";
import styled from "@emotion/styled";
import * as lscache from "lscache";

import {elevation} from "@pg-design/elevation";
import {borderRadius, flexAbsoluteCenter, onDesktop, p, pointer} from "@pg-design/helpers-css";
import {calculateRemSize} from "@pg-design/helpers-css-strings";
import {CloseIcon, HeartIcon} from "@pg-design/icons-module";
import {useIsMounted} from "@pg-mono/hooks";
import {useUserDevice} from "@pg-mono/user-device";
import {sessionCache} from "@pg-rp/session-cache";

import {IS_COOKIE_MESSAGE_READ_LS_KEY, IS_FAVOURITES_MODAL_CLOSED} from "../../../app/constants/storage_keys";
import {IRPStore} from "../../../app/rp_reducer";
import {useFavourites} from "../../hooks/use_favourites";
import {FavouritesInfoModalSubscribeInfo} from "./FavouritesInfoModalSubscribeInfo";

interface IProps {
    offerId?: number;
    propertyId?: number;
}

export const FavouritesInfoModal = (props: IProps) => {
    const hasFavModalBeenClosedInThisSession = sessionCache.get(IS_FAVOURITES_MODAL_CLOSED);
    const {isMobile} = useUserDevice();
    const [openModal, setOpenModal] = useState(false);
    const [cookiesMessageRead, setCookiesMessageRead] = useState(!!lscache.get(IS_COOKIE_MESSAGE_READ_LS_KEY));
    const isMounted = useIsMounted();

    const {favouritePropertiesIds, favouriteOfferIds, loaded} = useFavourites();
    const isSubscribed = useSelector((state: IRPStore) => state.subscribedRealEstates.isModalOpen);
    const bottomFixedElementHeight = useSelector((state: IRPStore) => state.ui.bottomFixedElementHeight) ?? 0;

    const uniqueFavouritePropertiesIds = Array.from(new Set(favouritePropertiesIds));
    const uniqueFavouriteOfferIds = Array.from(new Set(favouriteOfferIds));

    const hasAnyPropertiesToCompare = uniqueFavouritePropertiesIds.length > 0;
    const countedFavourites = hasAnyPropertiesToCompare
        ? uniqueFavouritePropertiesIds.length
        : uniqueFavouritePropertiesIds.length + uniqueFavouriteOfferIds.length;

    const openModalOnClick = () => {
        setOpenModal(true);
    };

    const closeModalOnClick = () => {
        setOpenModal(false);
        sessionCache.set(IS_FAVOURITES_MODAL_CLOSED, true);
    };

    useEffect(() => {
        //  handling modal bottom position if user still have cookie message
        if (isMounted && countedFavourites > 0) {
            const checkCookiesMessage = setInterval(() => {
                setCookiesMessageRead(!!lscache.get(IS_COOKIE_MESSAGE_READ_LS_KEY));

                if (lscache.get(IS_COOKIE_MESSAGE_READ_LS_KEY)) {
                    clearInterval(checkCookiesMessage);
                }
            }, 500);
        }
        if (!hasFavModalBeenClosedInThisSession && !isMobile && isSubscribed) {
            setOpenModal(true);
        }
    }, [isMounted, countedFavourites]);

    if ((!isMounted && !loaded) || countedFavourites === 0) {
        return null;
    }

    if (openModal) {
        return (
            <FavouritesListPopup bottomOffset={bottomFixedElementHeight} cookiesRead={cookiesMessageRead}>
                <span onClick={closeModalOnClick} css={closeIconHolder}>
                    <CloseIcon size="2" />
                </span>
                <FavouritesInfoModalSubscribeInfo countedFavourites={countedFavourites} offerId={props.offerId} propertyId={props.propertyId} />
            </FavouritesListPopup>
        );
    }

    return (
        <div css={favouritesCounter(cookiesMessageRead, bottomFixedElementHeight)} onClick={openModalOnClick}>
            {countedFavourites > 0 && <span css={favouritesCountBadge}>{countedFavourites}</span>}

            <HeartIcon size="2.5" css={[pointer, elevation(2)]} wrapperColor="white" wrapperSize="4" wrapperType="circle" />
        </div>
    );
};

// Styles
interface IFavouritesListTheme {
    bottomOffset: number;
    cookiesRead: boolean;
}

const FavouritesListPopup = styled.div<IFavouritesListTheme>`
    ${elevation(3)}
    ${p(2)}
    ${borderRadius(2)}
    position: fixed;
    right: 1.6rem;
    bottom: ${(props) => (props.cookiesRead ? `calc(${props.bottomOffset}px + 1.6rem)` : `${calculateRemSize(16)}`)};
    width: 258px;
    z-index: 15; // must be bigger than navigation z-index
    background-color: #fff;

    @media (min-width: ${(props) => props.theme.breakpoints.md}) {
        bottom: ${(props) => (props.cookiesRead ? "2.4rem" : "8.5rem")};
        right: 3.2rem;
        width: 280px;
    }
`;

const closeIconHolder = css`
    position: absolute;
    top: 1rem;
    right: 1.6rem;
    cursor: pointer;
`;

const favouritesCounter = (cookiesRead: boolean, bottomOffset: number) => () =>
    css`
        ${flexAbsoluteCenter};
        position: fixed;
        right: 1.6rem;
        bottom: ${cookiesRead ? `calc(${bottomOffset}px + 1.6rem)` : `${calculateRemSize(16)}`};
        z-index: 15; // must be bigger than navigation z-index
        cursor: pointer;
        ${pointer}
        ${elevation(2)}
        border-radius: 50%;

        ${onDesktop(css`
            bottom: ${cookiesRead ? "2.4rem" : "8.5rem"};
            right: 2.4rem;
        `)}
    `;

const favouritesCountBadge = (theme: Theme) => css`
    position: absolute;
    top: -0.5rem;
    right: -0.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${theme.colors.secondary};
    width: 2rem;
    height: 2rem;
    border-radius: 50%;
    background-color: ${theme.colors.primary};
`;
import {useEffect, useState} from "react";
import {useSelector} from "react-redux";
import {css, Theme} from "@emotion/react";
import styled from "@emotion/styled";
import * as lscache from "lscache";

import {elevation} from "@pg-design/elevation";
import {borderRadius, flexAbsoluteCenter, onDesktop, p, pointer} from "@pg-design/helpers-css";
import {calculateRemSize} from "@pg-design/helpers-css-strings";
import {CloseIcon, HeartIcon} from "@pg-design/icons-module";
import {useIsMounted} from "@pg-mono/hooks";
import {useUserDevice} from "@pg-mono/user-device";
import {sessionCache} from "@pg-rp/session-cache";

import {IS_COOKIE_MESSAGE_READ_LS_KEY, IS_FAVOURITES_MODAL_CLOSED} from "../../../app/constants/storage_keys";
import {IRPStore} from "../../../app/rp_reducer";
import {useFavourites} from "../../hooks/use_favourites";
import {FavouritesInfoModalSubscribeInfo} from "./FavouritesInfoModalSubscribeInfo";

interface IProps {
    offerId?: number;
    propertyId?: number;
}

export const FavouritesInfoModal = (props: IProps) => {
    const hasFavModalBeenClosedInThisSession = sessionCache.get(IS_FAVOURITES_MODAL_CLOSED);
    const {isMobile} = useUserDevice();
    const [openModal, setOpenModal] = useState(false);
    const [cookiesMessageRead, setCookiesMessageRead] = useState(!!lscache.get(IS_COOKIE_MESSAGE_READ_LS_KEY));
    const isMounted = useIsMounted();

    const {favouritePropertiesIds, favouriteOfferIds, loaded} = useFavourites();
    const isSubscribed = useSelector((state: IRPStore) => state.subscribedRealEstates.isModalOpen);
    const bottomFixedElementHeight = useSelector((state: IRPStore) => state.ui.bottomFixedElementHeight) ?? 0;

    const uniqueFavouritePropertiesIds = Array.from(new Set(favouritePropertiesIds));
    const uniqueFavouriteOfferIds = Array.from(new Set(favouriteOfferIds));

    const hasAnyPropertiesToCompare = uniqueFavouritePropertiesIds.length > 0;
    const countedFavourites = hasAnyPropertiesToCompare
        ? uniqueFavouritePropertiesIds.length
        : uniqueFavouritePropertiesIds.length + uniqueFavouriteOfferIds.length;

    const openModalOnClick = () => {
        setOpenModal(true);
    };

    const closeModalOnClick = () => {
        setOpenModal(false);
        sessionCache.set(IS_FAVOURITES_MODAL_CLOSED, true);
    };

    useEffect(() => {
        //  handling modal bottom position if user still have cookie message
        if (isMounted && countedFavourites > 0) {
            const checkCookiesMessage = setInterval(() => {
                setCookiesMessageRead(!!lscache.get(IS_COOKIE_MESSAGE_READ_LS_KEY));

                if (lscache.get(IS_COOKIE_MESSAGE_READ_LS_KEY)) {
                    clearInterval(checkCookiesMessage);
                }
            }, 500);
        }
        if (!hasFavModalBeenClosedInThisSession && !isMobile && isSubscribed) {
            setOpenModal(true);
        }
    }, [isMounted, countedFavourites]);

    if ((!isMounted && !loaded) || countedFavourites === 0) {
        return null;
    }

    if (openModal) {
        return (
            <FavouritesListPopup bottomOffset={bottomFixedElementHeight} cookiesRead={cookiesMessageRead}>
                <span onClick={closeModalOnClick} css={closeIconHolder}>
                    <CloseIcon size="2" />
                </span>
                <FavouritesInfoModalSubscribeInfo countedFavourites={countedFavourites} offerId={props.offerId} propertyId={props.propertyId} />
            </FavouritesListPopup>
        );
    }

    return (
        <div css={favouritesCounter(cookiesMessageRead, bottomFixedElementHeight)} onClick={openModalOnClick}>
            {countedFavourites > 0 && <span css={favouritesCountBadge}>{countedFavourites}</span>}

            <HeartIcon size="2.5" css={[pointer, elevation(2)]} wrapperColor="white" wrapperSize="4" wrapperType="circle" />
        </div>
    );
};

// Styles
interface IFavouritesListTheme {
    bottomOffset: number;
    cookiesRead: boolean;
}

const FavouritesListPopup = styled.div<IFavouritesListTheme>`
    ${elevation(3)}
    ${p(2)}
    ${borderRadius(2)}
    position: fixed;
    right: 1.6rem;
    bottom: ${(props) => (props.cookiesRead ? `calc(${props.bottomOffset}px + 1.6rem)` : `${calculateRemSize(16)}`)};
    width: 258px;
    z-index: 15; // must be bigger than navigation z-index
    background-color: #fff;

    @media (min-width: ${(props) => props.theme.breakpoints.md}) {
        bottom: ${(props) => (props.cookiesRead ? "2.4rem" : "8.5rem")};
        right: 3.2rem;
        width: 280px;
    }
`;

const closeIconHolder = css`
    position: absolute;
    top: 1rem;
    right: 1.6rem;
    cursor: pointer;
`;

const favouritesCounter = (cookiesRead: boolean, bottomOffset: number) => () =>
    css`
        ${flexAbsoluteCenter};
        position: fixed;
        right: 1.6rem;
        bottom: ${cookiesRead ? `calc(${bottomOffset}px + 1.6rem)` : `${calculateRemSize(16)}`};
        z-index: 15; // must be bigger than navigation z-index
        cursor: pointer;
        ${pointer}
        ${elevation(2)}
        border-radius: 50%;

        ${onDesktop(css`
            bottom: ${cookiesRead ? "2.4rem" : "8.5rem"};
            right: 2.4rem;
        `)}
    `;

const favouritesCountBadge = (theme: Theme) => css`
    position: absolute;
    top: -0.5rem;
    right: -0.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${theme.colors.secondary};
    width: 2rem;
    height: 2rem;
    border-radius: 50%;
    background-color: ${theme.colors.primary};
`;
